import * as styles from "./Radio.css";

import { clsx } from "clsx";
import type { JSX } from "react";

import { Radio as BaseRadio } from "@base-ui/react/radio";
import { Ripple } from "@frontend/components/atoms/Ripple";
import { useControlContext } from "@frontend/contexts";

import { useRadioGroupContext } from "./Radio.context";
import type { RadioIndicatorPosition, RadioProps } from "./Radio.types";
import { RADIO_RIPPLE_SPREAD } from "./constants";

type FrameColumns = NonNullable<
  NonNullable<Parameters<typeof styles.item>[0]>["columns"]
>;

function frameColumns(
  indicatorPosition: RadioIndicatorPosition,
  hasAddon: boolean,
): FrameColumns {
  if (indicatorPosition === "start") {
    return hasAddon ? "control-addon-content" : "control-content";
  }

  return hasAddon ? "addon-content-control" : "content-control";
}

export function Radio({
  size,
  layout,
  indicatorPosition,
  align,
  invalid,
  label,
  description,
  footer,
  children,
  className,
  ...props
}: RadioProps): JSX.Element {
  const inherited = useControlContext();
  const group = useRadioGroupContext();

  const resolvedSize = size ?? inherited.size;
  const resolvedLayout = layout ?? group.layout;
  const resolvedIndicatorPosition =
    indicatorPosition ?? group.indicatorPosition;
  const resolvedAlign = align ?? group.align;
  const resolvedInvalid = invalid ?? group.invalid;

  const isText = resolvedLayout === "text";
  const hasText = label !== undefined || description !== undefined;

  const control = (
    <BaseRadio.Root
      data-slot="radio"
      className={clsx(
        styles.radio({
          size: resolvedSize,
          layout: resolvedLayout,
          invalid: resolvedInvalid,
          indicatorPosition: resolvedIndicatorPosition,
        }),
        className,
      )}
      {...props}
    >
      <Ripple shape="circle" spread={RADIO_RIPPLE_SPREAD} center />
      <BaseRadio.Indicator keepMounted className={styles.indicator} />
    </BaseRadio.Root>
  );

  if (isText) {
    return (
      <label className={styles.textLabel}>
        {control}
        <span className={styles.labelText}>{label}</span>
      </label>
    );
  }

  const row = (
    <>
      {control}
      {children !== undefined && (
        <span className={styles.addon}>{children}</span>
      )}
      {hasText && (
        <span className={styles.content}>
          <span className={styles.title}>{label}</span>
          {description !== undefined && (
            <span className={styles.description}>{description}</span>
          )}
        </span>
      )}
    </>
  );

  if (footer === undefined) {
    return (
      <label
        className={styles.item({
          size: resolvedSize,
          layout: resolvedLayout,
          align: resolvedAlign,
          invalid: resolvedInvalid,
        })}
      >
        {row}
      </label>
    );
  }

  return (
    <div
      className={styles.item({
        size: resolvedSize,
        layout: resolvedLayout,
        invalid: resolvedInvalid,
        framed: true,
        columns: frameColumns(
          resolvedIndicatorPosition,
          children !== undefined,
        ),
      })}
    >
      <label
        className={styles.row({
          align: resolvedAlign,
          stacked: resolvedLayout === "tile",
        })}
      >
        {row}
      </label>

      <div
        className={styles.footer}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {footer}
      </div>
    </div>
  );
}
